// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useState, useCallback } from 'react'
import { ScrollText, RefreshCw, AlertTriangle, Bug, ShieldAlert, CircleAlert } from 'lucide-react'
import * as api from '../../api/client'
import { fmtRelative } from '../../lib/opsFormat'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// D-UI-2: what each column of the two tables shows, for its header funnel.
type ExceptionRow = NonNullable<NonNullable<api.OpsOverview['app_errors']>['top_exceptions']>[number]
type FailureRow = NonNullable<api.OpsOverview['reliability']['recent_failures']>[number]
const EXCEPTION_FUNNELS: FunnelColumn<ExceptionRow>[] = [
  { key: 'type',    label: 'Type',    value: e => e.type },
  { key: 'message', label: 'Message', value: e => e.message, kind: 'text' },
  { key: 'count',   label: 'Count',   value: e => e.count },
]
const FAILURE_FUNNELS: FunnelColumn<FailureRow>[] = [
  { key: 'action', label: 'Action', value: f => f.action },
  { key: 'role',   label: 'Role',   value: f => f.actor_role },
  { key: 'when',   label: 'When',   value: f => f.occurred_at },
]

/**
 * Logs & Errors — the fullest operational error view, to decide alerting from:
 *  1. App Insights exceptions (type + count)
 *  2. Recent error/warning log traces (App Insights)
 *  3. Audit failures (denied/failed/locked actions from the audit log)
 * All real, PHI-scrubbed. Query-on-demand (refreshable), not a live tail.
 */
export default function LogsErrorsTab() {
  const [ov, setOv] = useState<api.OpsOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'error' | 'warning'>('all')
  // Internal system logs (blob JSONL — the source of truth we own). App Insights
  // can be empty when the middleware handles exceptions before the host sees them;
  // this feed is what actually recorded them.
  const [sysEvents, setSysEvents] = useState<api.SystemLogEntry[]>([])

  const load = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true)
    setErr(null)
    try {
      // The overview call feeds ONLY the App Insights traces panel (`ov.app_errors`); the system-log
      // feed below is separate and is the source of truth we own. Keep them decoupled so a failure on
      // the ops surface degrades this tab to "logs, no traces" instead of blanking it — a single 403
      // on /ops/overview used to take the entire tab down for the whole support tier.
      const [overview, today] = await Promise.all([
        api.getOpsOverview().catch(() => null),
        api.getSystemLogs(),
      ])
      setOv(overview)
      let events = today.events ?? []
      // Cover a rolling 24h: pull yesterday's partition too when available.
      const yd = new Date(Date.now() - 86400000).toISOString().slice(0, 10)
      if ((today.available_dates ?? []).includes(yd)) {
        try { const y = await api.getSystemLogs({ date: yd }); events = [...(y.events ?? []), ...events] } catch { /* yesterday partition optional */ }
      }
      const cutoff = Date.now() - 86400000
      setSysEvents(events.filter(e => (e.level === 'error' || e.level === 'warn' || e.level === 'warning') && Date.parse(e.ts) >= cutoff)
        .sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts)).slice(0, 100))
    }
    catch (e: any) { setErr(e?.message || 'Failed to load logs') }
    finally { setLoading(false); setRefreshing(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const ae = ov?.app_errors
  const traces = (ae?.recent_traces || []).filter(t => filter === 'all' || t.severity === filter)

  // The header funnels filter the whole loaded set of each table.
  const excFx = useTableFunnels(ae?.top_exceptions, EXCEPTION_FUNNELS)
  const failFx = useTableFunnels(ov?.reliability.recent_failures, FAILURE_FUNNELS)

  return (
    <div style={{ maxWidth: 'var(--page-max, 1000px)' }}>
      <div style={head}>
        <div>
          <h2 style={h2}><ScrollText size={18} /> Logs &amp; Errors</h2>
          <p style={sub}>Application exceptions, error traces, and audit failures · last 24h</p>
        </div>
        <button onClick={() => load(true)} disabled={refreshing} style={refreshBtn}>
          <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : undefined }} /> Refresh
        </button>
      </div>

      {err && <div style={errorBanner}><CircleAlert size={16} />{err}</div>}

      {loading ? (
        <div style={loadingBox}><RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</div>
      ) : (
        <>
          {/* Summary counters */}
          <div style={counterRow}>
            <Counter icon={<Bug size={15} />} label="System errors (24h)" value={sysEvents.filter(e => e.level === 'error').length} tone={sysEvents.some(e => e.level === 'error') ? 'bad' : 'ok'} />
            <Counter icon={<Bug size={15} />} label="Exception types" value={ae?.top_exceptions?.length ?? 0} tone="bad" />
            <Counter icon={<AlertTriangle size={15} />} label="Server errors (5xx)" value={ae?.failed_requests_5xx ?? 0} tone={(ae?.failed_requests_5xx ?? 0) > 0 ? 'bad' : 'ok'} />
            <Counter icon={<ScrollText size={15} />} label="Error/warn traces" value={ae?.recent_traces?.length ?? 0} tone="warn" />
            <Counter icon={<ShieldAlert size={15} />} label="Audit failures" value={ov?.reliability.failed_actions_24h ?? 0} tone={(ov?.reliability.failed_actions_24h ?? 0) > 0 ? 'bad' : 'ok'} />
          </div>

          {/* App Insights availability */}
          {ae && !ae.available && (
            <div style={infoBox}>
              Live application logs unavailable{ae.reason ? ` — ${ae.reason}` : ''}. Needs Log Analytics Reader on the app identity. Audit failures below are unaffected.
            </div>
          )}

          {/* Internal system log (source of truth: blob JSONL written by the app itself) */}
          <section style={card}>
            <h3 style={h3}><ScrollText size={15} /> System log — errors &amp; warnings (internal, last 24h)</h3>
            {sysEvents.length > 0 ? (
              <div style={logList}>
                {sysEvents.map((e, i) => (
                  <div key={i} style={logLine}>
                    <span style={{ ...sevBadge, background: e.level === 'error' ? '#fee2e2' : '#fef3c7', color: e.level === 'error' ? '#991b1b' : '#92400e' }}>
                      {e.level}
                    </span>
                    <span style={logTime}>{fmtRelative(e.ts)}</span>
                    <span style={logMsg}>
                      <code style={{ fontSize: 11.5 }}>{e.category}.{e.code}</code>
                      {e.entity_type ? ` · ${e.entity_type}` : ''}
                      {e.detail?.exception ? ` — ${e.detail.exception}` : ''}
                      {e.detail?.at ? ` @ ${String(e.detail.at).split('/').pop()}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            ) : <Empty>No internal errors or warnings in the last 24 hours.</Empty>}
          </section>

          {/* Exceptions (Application Insights) */}
          <section style={card}>
            <h3 style={h3}><Bug size={15} /> Exceptions <span style={{ fontSize: 11, fontWeight: 400, color: '#64748b' }}>(Application Insights)</span></h3>
            {ae?.top_exceptions && ae.top_exceptions.length > 0 ? (
              <>
              {excFx.chips}
              <table {...excFx.tableProps} style={table}>
                <thead><tr><th style={th}>Type {excFx.funnel('type')}</th><th style={th}>Message {excFx.funnel('message')}</th><th style={thR}>Count {excFx.funnel('count')}</th></tr></thead>
                <tbody>
                  {excFx.rows.map((e, i) => (
                    <tr key={i}>
                      <td style={td}><code style={codeErr}>{e.type}</code></td>
                      <td style={{ ...td, color: '#475569' }}>{e.message || '—'}</td>
                      <td style={tdR}><strong>{e.count}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </>
            ) : <Empty>No exceptions in the last 24 hours.</Empty>}
          </section>

          {/* Log traces */}
          <section style={card}>
            <div style={sectionHead}>
              <h3 style={h3}><ScrollText size={15} /> Log traces</h3>
              <div style={filterRow}>
                {(['all', 'error', 'warning'] as const).map(f => (
                  <button key={f} onClick={() => setFilter(f)} style={{ ...chip, ...(filter === f ? chipActive : null) }}>{f}</button>
                ))}
              </div>
            </div>
            {traces.length > 0 ? (
              <div style={logList}>
                {traces.map((t, i) => (
                  <div key={i} style={logLine}>
                    <span style={{ ...sevBadge, background: t.severity === 'error' ? '#fee2e2' : '#fef3c7', color: t.severity === 'error' ? '#991b1b' : '#92400e' }}>
                      {t.severity}
                    </span>
                    <span style={logTime}>{fmtRelative(t.timestamp)}</span>
                    <span style={logMsg}>{t.message}</span>
                  </div>
                ))}
              </div>
            ) : <Empty>{ae?.available ? 'No error or warning traces in the last 24 hours.' : 'Traces unavailable.'}</Empty>}
          </section>

          {/* Audit failures */}
          <section style={card}>
            <h3 style={h3}><ShieldAlert size={15} /> Audit failures</h3>
            {ov?.reliability.recent_failures && ov.reliability.recent_failures.length > 0 ? (
              <>
              {failFx.chips}
              <table {...failFx.tableProps} style={table}>
                <thead><tr><th style={th}>Action {failFx.funnel('action')}</th><th style={th}>Role {failFx.funnel('role')}</th><th style={thR}>When {failFx.funnel('when')}</th></tr></thead>
                <tbody>
                  {failFx.rows.map((f, i) => (
                    <tr key={i}>
                      <td style={td}><code style={codeErr}>{f.action}</code></td>
                      <td style={{ ...td, color: '#475569' }}>{f.actor_role || '—'}</td>
                      <td style={tdR}>{fmtRelative(f.occurred_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </>
            ) : <Empty>No audit failures in the last 24 hours.</Empty>}
          </section>

          <p style={note}>{ae?.note || 'Audit failures are recorded in the tamper-evident audit log.'}</p>
        </>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}

function Counter({ icon, label, value, tone }: { icon: React.ReactNode; label: string; value: number; tone: 'ok' | 'warn' | 'bad' }) {
  const c = tone === 'ok' ? '#059669' : tone === 'warn' ? '#b45309' : '#dc2626'
  return (
    <div style={counter}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b' }}>{icon}<span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span></div>
      <div style={{ fontSize: 26, fontWeight: 800, color: value > 0 ? c : '#0f172a', marginTop: 4 }}>{value}</div>
    </div>
  )
}
function Empty({ children }: { children: React.ReactNode }) { return <div style={empty}>{children}</div> }


const head: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }
const h2: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, fontWeight: 800, margin: 0, color: '#0f172a' }
const sub: React.CSSProperties = { fontSize: 13, color: '#64748b', margin: '4px 0 0' }
const refreshBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#334155', cursor: 'pointer' }
const errorBanner: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: 12, borderRadius: 10, marginBottom: 16, fontSize: 13 }
const infoBox: React.CSSProperties = { background: '#f1f5f9', border: '1px dashed #cbd5e1', borderRadius: 8, padding: 12, fontSize: 13, color: '#475569', marginBottom: 16 }
const loadingBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: 40, justifyContent: 'center', color: '#64748b' }
const counterRow: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginBottom: 16 }
const counter: React.CSSProperties = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14 }
const card: React.CSSProperties = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, marginBottom: 16 }
const sectionHead: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }
const h3: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 7, fontSize: 14, fontWeight: 700, margin: '0 0 12px', color: '#0f172a' }
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid #e2e8f0', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: '#94a3b8' }
const thR: React.CSSProperties = { ...th, textAlign: 'right' }
const td: React.CSSProperties = { padding: '7px 8px', borderBottom: '1px solid #f1f5f9' }
const tdR: React.CSSProperties = { ...td, textAlign: 'right', whiteSpace: 'nowrap' }
const codeErr: React.CSSProperties = { fontSize: 12, color: '#b91c1c', background: '#fef2f2', padding: '1px 6px', borderRadius: 4, fontFamily: 'ui-monospace,monospace' }
const filterRow: React.CSSProperties = { display: 'flex', gap: 4, marginBottom: 12 }
const chip: React.CSSProperties = { fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', textTransform: 'capitalize' }
const chipActive: React.CSSProperties = { background: '#0e7490', color: '#fff', borderColor: '#0e7490' }
const logList: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 2, fontFamily: 'ui-monospace,monospace', fontSize: 12.5 }
const logLine: React.CSSProperties = { display: 'flex', alignItems: 'baseline', gap: 10, padding: '5px 8px', borderBottom: '1px solid #f8fafc' }
const sevBadge: React.CSSProperties = { fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 4, textTransform: 'uppercase', flexShrink: 0 }
const logTime: React.CSSProperties = { color: '#94a3b8', fontSize: 11, whiteSpace: 'nowrap', flexShrink: 0, minWidth: 60 }
const logMsg: React.CSSProperties = { color: '#1e293b', wordBreak: 'break-word' }
const empty: React.CSSProperties = { fontSize: 13, color: '#94a3b8', padding: '8px 0' }
const note: React.CSSProperties = { fontSize: 11, color: '#94a3b8', marginTop: 4, lineHeight: 1.5 }
